import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { InfiniteList } from "components/list/infinity-list";
import { IGrantVestmentRequest } from "interfaces/grant-vestment-request";
import { UpdatedValues } from "pages/grant-vestments/update";
import { moneyFormat } from "utils/money-format";
import { useState } from "react";

interface ModalProps {
  visible: boolean;
  close: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: any;
  onFinish: any;
  UpdatedValues: UpdatedValues;
  grantVestmentId: number;
  setValue: any;
}

export const UpdateGrantVestmentConfirmationModal: React.FC<ModalProps> = ({
  visible,
  close,
  handleSubmit,
  onFinish,
  UpdatedValues,
  grantVestmentId,
  setValue,
}) => {
  const [hasPendingRequests, setHasPendingRequests] = useState(false);
  const [hasApprovedRequests, setHasApprovedRequests] = useState(false);
  const [sendClosingEmails, setSendClosingEmails] = useState(false);

  // Check if status is being changed to CLOSED
  const isClosingGrantVestment =
    UpdatedValues.status.current?.toString() === "CLOSED";

  // Confirm button should be disabled if there are pending or approved requests when closing
  const isConfirmDisabled =
    isClosingGrantVestment && (hasPendingRequests || hasApprovedRequests);

  return (
    <Dialog open={visible} onClose={() => close(false)}>
      <DialogTitle color="primary">
        GrantVestment Updates confirmation
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography>
          Are you sure you want to confirm the following changes?
        </Typography>
        <ul>
          {UpdatedValues.deadline.current?.toString().split("T")[0] !==
            UpdatedValues.deadline.prev?.toString().split("T")[0] && (
            <li>
              <Typography>
                {" "}
                {`Deadline is updated from ${
                  UpdatedValues.deadline?.prev?.toString().split("T")[0]
                } to ${
                  UpdatedValues.deadline?.current?.toString().split("T")[0]
                }`}
              </Typography>
            </li>
          )}
          {UpdatedValues.goalAmount.current !==
            UpdatedValues.goalAmount.prev && (
            <li>
              <Typography>
                {" "}
                {`Goal amount is updated from ${UpdatedValues.goalAmount?.prev} to ${UpdatedValues.goalAmount?.current}`}
              </Typography>
            </li>
          )}
          {UpdatedValues.allOrNothing.current?.toString() !==
            UpdatedValues.allOrNothing.prev?.toString() && (
            <li>
              <Typography>
                {" "}
                {UpdatedValues.allOrNothing.current?.toString() !==
                  UpdatedValues.allOrNothing.prev?.toString() &&
                  `All or Nothing is updated from ${UpdatedValues.allOrNothing?.prev
                    ?.toString()
                    .toUpperCase()} to ${UpdatedValues.allOrNothing?.current
                    ?.toString()
                    .toUpperCase()}`}
                <Typography
                  paragraph
                  variant="smallCopyBold"
                  marginY={0.5}
                  sx={{ color: "neutral.lighter" }}
                >
                  {" "}
                  {UpdatedValues.allOrNothing.current?.toString() !==
                    UpdatedValues.allOrNothing.prev?.toString() &&
                    UpdatedValues.allOrNothing.current?.toString() === "true" &&
                    `By making it True, users with pending or approved requests will be refunded and notified in case the GrantVestment closes and doesn't reach the goal. Requests that were already processed cannot be reversed.`}
                </Typography>
                <Typography
                  paragraph
                  variant="smallCopyBold"
                  sx={{ color: "neutral.lighter" }}
                >
                  {" "}
                  {UpdatedValues.allOrNothing.current?.toString() !==
                    UpdatedValues.allOrNothing.prev?.toString() &&
                    UpdatedValues.allOrNothing.current?.toString() ===
                      "false" &&
                    `By making it False, users will NOT be refunded or notified in case the GrantVestment closes and doesn't reach the goal.`}
                </Typography>{" "}
              </Typography>
            </li>
          )}
          {UpdatedValues.status.current?.toString() !==
            UpdatedValues.status.prev?.toString() && (
            <li>
              <Typography>
                {" "}
                {UpdatedValues.status.current?.toString() !==
                  UpdatedValues.status.prev?.toString() &&
                  `Status is updated from ${UpdatedValues.status?.prev} to ${UpdatedValues.status?.current}`}
                <Typography
                  paragraph
                  variant="smallCopyBold"
                  marginY={0.5}
                  sx={{ color: "neutral.lighter" }}
                >
                  {" "}
                  {UpdatedValues.status.current?.toString() !==
                    UpdatedValues.status.prev?.toString() &&
                    UpdatedValues.status.current?.toString() === "CLOSED" &&
                    `By Closing this GrantVestment, users with pending or approved requests will ${
                      UpdatedValues.successfully
                        ? `be notified`
                        : !UpdatedValues.successfully &&
                          UpdatedValues.allOrNothing?.current?.toString() ===
                            "true"
                        ? `be notified and refunded since it didn't reach the goal as all-or-nothing is true. Only pending and approved requests can be reversed - processed requests cannot be reversed.`
                        : `not be refunded even though it didn't reach the goal as all-or-nothing is false.`
                    }`}
                  {UpdatedValues.status.current?.toString() !==
                    UpdatedValues.status.prev?.toString() &&
                    UpdatedValues.status.current?.toString() === "RAISING" &&
                    `Updating to raising will make users able to GrantVest and contribute to it.`}
                  {UpdatedValues.status.current?.toString() !==
                    UpdatedValues.status.prev?.toString() &&
                    UpdatedValues.status.current?.toString() === "DRAFT" &&
                    `Updating to draft will hide the GrantVestment, users will not be able to GrantVest and contribute to it.`}
                  {UpdatedValues.status.current?.toString() !==
                    UpdatedValues.status.prev?.toString() &&
                    UpdatedValues.status.current?.toString() === "CANCELED" &&
                    `By Canceling this GrantVestment, users with pending and approved requests will be refunded. Processed requests cannot be reversed.`}
                </Typography>
              </Typography>
            </li>
          )}
        </ul>
        {isClosingGrantVestment && (
          <>
            {" "}
            <Box>
              <Typography>
                Pending requests{" "}
                <Typography
                  paragraph
                  variant="smallCopyBold"
                  marginY={0.5}
                  sx={{ color: "neutral.lighter" }}
                >
                  {UpdatedValues.allOrNothing?.current?.toString() !== "true"
                    ? " The GrantVestment can't be closed until you approve all the following requests"
                    : " If the grantvestment is closed, the following requests will be refunded since all-or-nothing is true"}
                </Typography>
              </Typography>
              <InfiniteList<IGrantVestmentRequest>
                endpoint={`grant-vestments/${grantVestmentId}/requests`}
                isTable={true}
                limit={10}
                columns={[
                  {
                    field: "user",
                    header: "User",
                    renderCell: function render({ user }) {
                      return `By ${user.firstName} ${user.lastName}`;
                    },
                  },
                  {
                    field: "fund",
                    header: "Fund Name",
                    renderCell: function render({ fund: fundAccount }) {
                      return fundAccount.fundAccountName;
                    },
                  },

                  {
                    header: "Amount",
                    renderCell: function render({ appliedAmount }) {
                      return (
                        <Typography
                          sx={{
                            px: 1,
                            py: 1,
                          }}
                          variant="bodyCopyBold"
                          gutterBottom
                        >
                          ${moneyFormat(appliedAmount)}
                        </Typography>
                      );
                    },
                  },
                ]}
                additionalFilters={[
                  {
                    field: "filter[status]",
                    operator: "eq",
                    value: "PENDING",
                  },
                  {
                    field: "filter[transfer]",
                    operator: "eq",
                    value: "false",
                  },
                ]}
                tableProps={{
                  stickyHeader: true,
                  headerBgColor: "grey.200",
                  responsive: true,
                }}
                onDataChange={(data) => setHasPendingRequests(data.length > 0)}
              />

              <Typography>
                Approved requests{" "}
                <Typography
                  paragraph
                  variant="smallCopyBold"
                  marginY={0.5}
                  sx={{ color: "neutral.lighter" }}
                >
                  {UpdatedValues.allOrNothing?.current?.toString() !== "true"
                    ? "The GrantVestment can't be closed until you process all the following requests"
                    : "If the grantvestment is closed, the following requests will be refunded since all-or-nothing is true"}
                </Typography>{" "}
              </Typography>
              <InfiniteList<IGrantVestmentRequest>
                endpoint={`grant-vestments/${grantVestmentId}/requests`}
                isTable={true}
                limit={5}
                columns={[
                  {
                    field: "user",
                    header: "User",
                    renderCell: function render({ user }) {
                      return `By ${user.firstName} ${user.lastName}`;
                    },
                  },
                  {
                    field: "fund",
                    header: "Fund Name",
                    renderCell: function render({ fund: fundAccount }) {
                      return fundAccount.fundAccountName;
                    },
                  },

                  {
                    header: "Amount",
                    renderCell: function render({ appliedAmount }) {
                      return (
                        <Typography
                          sx={{
                            px: 1,
                            py: 1,
                          }}
                          variant="bodyCopyBold"
                          gutterBottom
                        >
                          ${moneyFormat(appliedAmount)}
                        </Typography>
                      );
                    },
                  },
                ]}
                additionalFilters={[
                  {
                    field: "filter[status]",
                    operator: "eq",
                    value: "APPROVED",
                  },
                  {
                    field: "filter[transfer]",
                    operator: "eq",
                    value: "false",
                  },
                ]}
                tableProps={{
                  stickyHeader: true,
                  headerBgColor: "grey.200",
                  responsive: true,
                }}
                onDataChange={(data) => setHasApprovedRequests(data.length > 0)}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <Divider />
      {isClosingGrantVestment && (
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              onChange={(e) => setSendClosingEmails(e.target.checked)}
              sx={{ ml: 3 }}
            />
          }
          label="Send closing email to contributors"
        />
      )}{" "}
      <DialogActions>
        <>
          <Button
            variant="contained"
            disabled={
              UpdatedValues.allOrNothing?.current?.toString() !== "true" &&
              isConfirmDisabled
            }
            onClick={() => {
              setValue("sendClosingEmails", sendClosingEmails);
              handleSubmit(onFinish)();
              close(false);
            }}
          >
            {isClosingGrantVestment
              ? UpdatedValues.allOrNothing?.current?.toString() === "true"
                ? "Apply changes, Close and Refund"
                : "Apply changes and Close"
              : "Apply changes"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              close(false);
            }}
          >
            Cancel
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};
